import {
  Card,
  Input,
  Button,
  Typography,
  Textarea,
  Tabs,
  TabsHeader,
  TabsBody,
  Tab,
  TabPanel,
  Spinner,
} from "@material-tailwind/react";
import Navbar from "../../component/Navbar";
import { dataPost, dataGet } from "../../network";
import {
  postManualData,
  postNmsData,
  nmsLocations,
  nmsSync,
  nmsNotams,
} from "../../network/const";
import { Dialog } from "../../component/items/Dialog";
import { dialogDataTypes } from "../../component/items/itemDataType";
import { useState, useEffect, useRef } from "react";
import axios from "axios";

export function LoadData() {
  const [open, setOpen] = useState(false);
  const [dialogData, setDialogData] = useState({
    title: "",
    btnText: "",
    description: "",
  });
  const [formState, setFormState] = useState({
    email: "notam@info.com",
    identification: "",
    data: "",
  });

  // NMS State
  const [locations, setLocations] = useState<any[]>([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedLocationDesc, setSelectedLocationDesc] = useState("");
  const [syncLoading, setSyncLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [syncedNotams, setSyncedNotams] = useState<any[]>([]);
  const [saveAllLoading, setSaveAllLoading] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const handleChange = (e: any) => {
    setFormState({ ...formState, [e.target.id]: e.target.value });
  };
  const initDialog = (data: dialogDataTypes) => {
    setOpen(true);
    setDialogData({
      title: data.title,
      btnText: data.btnText,
      description: data.description,
    });
  };

  const fetchLocations = async (search = "") => {
    try {
      const response = await axios.get(nmsLocations, {
        params: { search, limit: 50 },
      });
      // API returns { count: number, rows: [] }
      setLocations(response.data.rows || []);
    } catch (error) {
      console.error("Failed to fetch locations", error);
    }
  };

  const fetchSyncedNotams = async (locationId: string) => {
    try {
      const response = await dataGet(nmsNotams, { locationId });
      if (response.status) {
        setSyncedNotams(response.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch synced NOTAMs", error);
    }
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  // Debounce search
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchLocations(searchTerm);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  // Click outside handler
  useEffect(() => {
    function handleClickOutside(event: any) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [wrapperRef]);

  const handleSelectLocation = (loc: any) => {
    setSelectedLocation(loc.locationId);
    setSelectedLocationDesc(loc.description);
    setSearchTerm(`${loc.locationId} - ${loc.description}`);
    setShowDropdown(false);
    setSyncedNotams([]); // Clear previous results
  };

  const syncNmsData = async () => {
    if (!selectedLocation) return;
    setSyncLoading(true);

    const response = await dataPost(
      nmsSync,
      {
        locationId: selectedLocation,
      },
      { silent: false },
    );

    if (response.status) {
      const count = response.data.count;
      let message = "";
      if (count > 0) {
        message = `${count} NOTAM(s) successfully synced for aerodrome ${selectedLocationDesc} (${selectedLocation}).`;
        // Fetch the synced NOTAMs to display them
        await fetchSyncedNotams(selectedLocation);
      } else {
        message = `No active NOTAMs were found for aerodrome ${selectedLocationDesc} (${selectedLocation}) at this time.`;
        setSyncedNotams([]);
        initDialog({
          title: response.title,
          description: message,
          btnText: "OK",
        });
      }
    } else {
      initDialog({
        title: response.title || "Sync Failed",
        description: response.message || "Unknown error",
        btnText: "OK",
      });
    }
    setSyncLoading(false);
  };

  const submitData = async () => {
    const data = await dataPost(postManualData, formState);

    if (data.status) {
      initDialog({
        description: data.message,
        title: data.title,
        btnText: "OK",
      });
      // setFormState({ email: "notam@info.com", identification: "", data: "" });
    } else {
      console.error("NOTAM submission failed:", data.message);
      initDialog({
        description: data.message.includes("Invalid NOTAM format")
          ? `NOTAM Validation Failed:\n${data.message}`
          : data.message,
        title: data.title || "Error",
        btnText: "OK",
      });
    }
  };

  const saveSingleNotam = async (notam: any) => {
    const payload = {
      email: "notam@info.com",
      notams: [
        {
          notamId: notam.notamId,
          text: notam.text,
        },
      ],
    };

    const response = await dataPost(postNmsData, payload, { silent: false });

    if (response.status) {
      initDialog({
        description: response.message,
        title: response.title,
        btnText: "OK",
      });
      // Remove from list if saved successfully
      setSyncedNotams((prev) => prev.filter((n) => n.id !== notam.id));
    } else {
      initDialog({
        description: response.message,
        title: response.title || "Error",
        btnText: "OK",
      });
    }
  };

  const saveAllNotams = async () => {
    if (!syncedNotams.length) return;
    setSaveAllLoading(true);

    const payload = {
      email: "notam@info.com",
      notams: syncedNotams.map((n) => ({
        notamId: n.notamId,
        text: n.text,
      })),
    };

    const response = await dataPost(postNmsData, payload, { silent: false });

    if (response.status) {
      const successNotamIds = response.data.success.map((s: any) => s.notamId);

      // Remove successful ones from the local list
      setSyncedNotams((prev) =>
        prev.filter((n) => !successNotamIds.includes(n.notamId)),
      );

      const successCount = response.data.success.length;
      const failedCount = response.data.failed.length;

      let message = `${successCount} NOTAM(s) successfully saved.`;
      if (failedCount > 0) {
        message += `\n${failedCount} NOTAM(s) failed or already exist.`;
      }

      initDialog({
        description: message,
        title:
          failedCount === 0
            ? "Batch Process Complete"
            : "Partial Batch Success",
        btnText: "OK",
      });
    } else {
      initDialog({
        description: response.message || "Failed to process batch",
        title: "Error",
        btnText: "OK",
      });
    }

    setSaveAllLoading(false);
  };

  const handleNotamTextChange = (id: number, newText: string) => {
    setSyncedNotams((prev) =>
      prev.map((n) => (n.id === id ? { ...n, text: newText } : n)),
    );
  };

  const removeNotamFromList = (id: number) => {
    setSyncedNotams((prev) => prev.filter((n) => n.id !== id));
  };

  return (
    <>
      <Navbar />
      <div className="pt-24 pb-10 px-4 w-full min-h-screen bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          {/* Two-column layout */}
          <div className="flex flex-col lg:flex-row gap-8 items-start justify-center">
            {/* Left: Form */}
            <Card
              shadow={true}
              className="w-full lg:flex-1 p-6 min-h-[500px] !overflow-visible"
              style={{ overflow: "visible" }}
            >
              <Typography
                variant="h4"
                color="blue-gray"
                className="text-center mb-6"
              >
                Load Data
              </Typography>

              <Tabs value="manual" className="overflow-visible">
                <TabsHeader>
                  <Tab value="manual">Manual Entry</Tab>
                  <Tab value="nms">FAA NOTAMs</Tab>
                </TabsHeader>
                <TabsBody
                  className="!overflow-visible"
                  style={{ overflow: "visible" }}
                >
                  <TabPanel value="manual" className="px-0">
                    <Typography
                      color="gray"
                      className="text-center mb-6 font-normal"
                    >
                      Please fill in the NOTAM data below
                    </Typography>
                    <form className="flex flex-col gap-6">
                      <Input
                        size="lg"
                        label="Identification"
                        id="identification"
                        crossOrigin={undefined}
                        onChange={handleChange}
                        value={formState.identification}
                      />
                      <Textarea
                        label="Insert NOTAM data here"
                        rows={10}
                        id="data"
                        onChange={handleChange}
                        value={formState.data}
                      />
                      <Button
                        fullWidth
                        onClick={submitData}
                        disabled={!formState.identification || !formState.data}
                      >
                        Submit Data
                      </Button>
                    </form>
                  </TabPanel>
                  <TabPanel
                    value="nms"
                    className="px-0 !overflow-visible"
                    style={{ overflow: "visible" }}
                  >
                    <Typography
                      color="gray"
                      className="text-center mb-0 font-normal"
                    >
                      Select an Aerodrome to FAA NOTAMs
                    </Typography>
                    <div className="flex flex-col gap-6 relative p-4">
                      <div className="relative" ref={wrapperRef}>
                        <Input
                          label="Search & Select Location"
                          value={searchTerm}
                          onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setShowDropdown(true);
                            setSelectedLocation(""); // Reset selection on type
                          }}
                          onFocus={() => setShowDropdown(true)}
                          crossOrigin={undefined}
                        />
                        {showDropdown && locations.length > 0 && (
                          <Card className="absolute top-full mt-1 left-0 w-full max-h-60 overflow-y-auto z-50 shadow-xl border border-blue-gray-100 rounded-md">
                            <ul className="flex flex-col p-1">
                              {locations.map((loc: any) => (
                                <li
                                  key={loc.locationId}
                                  className="px-4 py-2 hover:bg-blue-50 cursor-pointer rounded-md text-sm transition-colors"
                                  onClick={() => handleSelectLocation(loc)}
                                >
                                  <span className="font-bold text-blue-gray-900">
                                    {loc.locationId}
                                  </span>{" "}
                                  <span className="text-gray-600">
                                    - {loc.description}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </Card>
                        )}
                      </div>
                      {syncedNotams.length === 0 && (
                        <Button
                          fullWidth
                          onClick={syncNmsData}
                          disabled={!selectedLocation || syncLoading}
                          className="mt-0 flex items-center justify-center gap-2"
                        >
                          {syncLoading && <Spinner className="h-4 w-4" />}
                          {syncLoading ? "Syncing..." : "FAA NOTAMs"}
                        </Button>
                      )}
                    </div>

                    {/* FAA NOTAMs List Area */}
                    {syncedNotams.length > 0 && (
                      <div className="mt-8 border-t border-gray-200 pt-6">
                        <div className="flex justify-between items-center mb-4 px-4">
                          <Typography variant="h6" color="blue-gray">
                            Synced FAA NOTAMs ({syncedNotams.length})
                          </Typography>
                          <Button
                            size="sm"
                            onClick={saveAllNotams}
                            disabled={saveAllLoading}
                            className="flex items-center gap-2"
                          >
                            {saveAllLoading && <Spinner className="h-4 w-4" />}
                            Submit All
                          </Button>
                        </div>
                        <div className="max-h-[500px] overflow-y-auto flex flex-col gap-4 px-4">
                          {syncedNotams.map((notam) => (
                            <Card
                              key={notam.id}
                              className="p-4 border border-gray-100 shadow-sm"
                            >
                              <div className="flex justify-between items-start gap-4 mb-2">
                                <Typography
                                  variant="small"
                                  color="blue"
                                  className="font-bold"
                                >
                                  {notam.notamId}
                                </Typography>
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    color="red"
                                    variant="text"
                                    onClick={() =>
                                      removeNotamFromList(notam.id)
                                    }
                                    className="px-2"
                                  >
                                    Remove
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outlined"
                                    onClick={() => saveSingleNotam(notam)}
                                    className="flex-shrink-0"
                                  >
                                    Submit
                                  </Button>
                                </div>
                              </div>
                              <Textarea
                                rows={7}
                                value={notam.text}
                                onChange={(e) =>
                                  handleNotamTextChange(
                                    notam.id,
                                    e.target.value,
                                  )
                                }
                                className="!text-xs font-mono"
                                label="Edit NOTAM Text"
                              />
                            </Card>
                          ))}
                        </div>
                      </div>
                    )}
                  </TabPanel>
                </TabsBody>
              </Tabs>
            </Card>

            {/* Right: Note Card */}
            <Card
              shadow={false}
              className="w-full lg:w-[400px] p-6 bg-blue-50 border border-blue-100 rounded-xl"
            >
              <Typography
                variant="h6"
                color="blue"
                className="mb-4 flex items-center gap-2"
              >
                <span>⚠️</span> Instructions
              </Typography>
              <Typography
                variant="small"
                color="blue-gray"
                className="text-xs whitespace-pre-line leading-relaxed opacity-80 font-medium"
              >
                Only Full ICAO NOTAM Format with brackets is accepted.
                {"\n\n"}✅ Single NOTAM Example:
                {"\n"}(A1736/25 NOTAMN
                {"\n"}Q)OMAE/QWULW/IV/BO/AW/000/003/2427N05435E001
                {"\n"}A)OMAA B)2506220700 C)2506221000
                {"\n"}E)UAS ACT WILL TAKE PLACE WI AREA BOUNDED BY LINES
                JOINING:
                {"\n"}242648.59N 0543505.60E
                {"\n"}242650.56N 0543517.50E
                {"\n"}242642.68N 0543520.28E
                {"\n"}242640.57N 0543508.23E
                {"\n"}242648.59N 0543505.60E
                {"\n"}F)SFC G)100FT AGL)
                {"\n\n"}✅ Multiple NOTAMs Example:
                {"\n"}(A1736/25 NOTAMN
                {"\n"}Q)OMAE/QWULW/IV/BO/AW/000/003/2427N05435E001
                {"\n"}A)OMAA B)2506220700 C)2506221000
                {"\n"}E)UAS ACT WILL TAKE PLACE WI AREA BOUNDED BY LINES
                JOINING:
                {"\n"}242648.59N 0543505.60E
                {"\n"}242650.56N 0543517.50E
                {"\n"}242642.68N 0543520.28E
                {"\n"}242640.57N 0543508.23E
                {"\n"}242648.59N 0543505.60E
                {"\n"}F)SFC G)100FT AGL)
                {"\n\n"}(A1737/25 NOTAMN
                {"\n"}Q)OMDB/QWULW/IV/BO/W/000/008/231402N0723746E001
                {"\n"}A)OMDB B)2509210000 C)2509212359
                {"\n"}E)UAS ACT WILL TAKE PLACE WI AREA BTN FLW POINTS:
                {"\n"}231402.00N 0723746.00E
                {"\n"}231420.00N 0723950.00E
                {"\n"}231430.00N 0723740.00E
                {"\n"}F)SFC G)800FT AGL)
                {"\n\n"}👉 Each NOTAM must start with (AXXXX/YY NOTAM...) in
                ICAO format.
                {"\n"}👉 Duplicate NOTAM numbers will be skipped automatically.
              </Typography>
            </Card>
          </div>

          {/* Dialog */}
          <Dialog
            open={open}
            handleOpen={() => setOpen(!open)}
            title={dialogData.title}
            description={dialogData.description}
            buttonText={dialogData.btnText}
          />
        </div>
      </div>
    </>
  );
}
